import React, { useCallback, useEffect } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';

import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { componentWithFG } from '@confluence/feature-gate-component';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';

import { IssueCreateWarningDialog } from '../work-create-common/IssueCreateWarningDialog';
import type { IssueCreateSidePanelProps } from '../../__types__/IssueCreateSidePanel';
import { IssueCreateAiLoadingSidebar } from '../ai-work-create/IssueCreateAiLoadingSidebar';
import {
	useAiIssueCreationEnablementState,
	useIssueCreateSidePanelContext,
} from '../../providers/IssueCreateSidePanelContextProvider';
import { SingleIssueCreateProvidersWrapper } from '../../providers/SingleIssueCreateProvidersWrapper';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper';
import { useGetWarningDialogActions } from '../../utils/useGetWarningDialogActions';

import { IssueCreateSidePanelBody } from './IssueCreateSidePanelBody';
import { IssueCreateSidePanelFooter } from './IssueCreateSidePanelFooter';
import { IssueCreateSidePanelHeader } from './IssueCreateSidePanelHeader';

export const sidePanelTestId = 'issue-create-side-panel';
export const ISSUE_CREATE_SIDE_PANEL_ID = 'issue-create-side-panel';
export const ISSUE_CREATE_SIDE_PANEL_WIDTH = 320;

const sidePanelStyle = xcss({
	position: 'relative',
});

const IssueCreateSidePanelComponent = ({ onClose, flags }: IssueCreateSidePanelProps) => {
	const { showSidePanel, hideSidePanel } = useSidePanelStateActions();
	const { isWarningDialogOpen, handleConfirm, handleCancel } = useGetWarningDialogActions(onClose);

	const [state, actions] = useIssueCreateSidePanelContext();
	const { aiState, isCreatedWithAi, aiAnalyticsContext } = state;
	const aiIssueCreationEnablement = useAiIssueCreationEnablementState();
	const { trackAIInteractionDismiss } = useAIEventsInstrumentation();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const shouldShowAiFooter = isCreatedWithAi && aiState !== 'error';

	const isAiLoading = aiState === 'loading';
	const handleOnClose = useCallback(
		(isCreateSuccess?: boolean) => {
			if (isCreatedWithAi && !isCreateSuccess) {
				trackAIInteractionDismiss({ attributes: aiAnalyticsContext });
			}

			if (!isCreateSuccess) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'confluenceHighlightIssueCreate',
						action: 'closed',
						actionSubject: 'modalDialog',
						actionSubjectId: 'linkCreate',
						type: 'ui',
						attributes: {
							isManuallyCreated: !isCreatedWithAi,
							isCreatedWithAi,
							pageMode: state.pageMode,
							hasEditPermission: state.hasEditPermission,
							isAiLoading,
							aiEnablement: aiIssueCreationEnablement?.enablement,
						},
					},
				}).fire();
			}

			hideSidePanel();
			onClose();
			void actions.setAiState('idle');
		},
		[
			isCreatedWithAi,
			hideSidePanel,
			onClose,
			actions,
			trackAIInteractionDismiss,
			aiAnalyticsContext,
			createAnalyticsEvent,
			state.pageMode,
			isAiLoading,
			aiIssueCreationEnablement?.enablement,
			state.hasEditPermission,
		],
	);

	const handleOnConfirm = useCallback(() => {
		void actions.setAiState('idle');
		handleConfirm();
	}, [actions, handleConfirm]);

	useEffect(() => {
		showSidePanel();
	}, [showSidePanel]);

	return (
		<Box testId={sidePanelTestId} xcss={sidePanelStyle}>
			<SidePanelWrapper>
				<IssueCreateAiLoadingSidebar>
					<IssueCreateSidePanelHeader onClose={handleOnClose} />
					<IssueCreateSidePanelBody onClose={handleOnClose} flags={flags} />
					{shouldShowAiFooter && <IssueCreateSidePanelFooter />}
				</IssueCreateAiLoadingSidebar>
			</SidePanelWrapper>
			{isWarningDialogOpen && (
				<IssueCreateWarningDialog
					onCancel={handleCancel}
					onConfirm={handleOnConfirm}
					issuesCount={1}
				/>
			)}
		</Box>
	);
};

const IssueCreateSidePanelComponentWithProvider = ({
	onClose,
	flags,
}: IssueCreateSidePanelProps) => {
	return (
		<SingleIssueCreateProvidersWrapper>
			<AIEventsInstrumentationProvider
				config={{
					// Required configruation
					product: 'confluence',
					subproduct: 'jira',
					aiFeatureName: 'confluenceAiWorkCreation',
					proactiveGeneratedAI: 0,
					userGeneratedAI: 1,
					// Optional configuration
					source: 'confluenceHighlightIssueCreate',
					skipAISessionValidation: true,
				}}
			>
				<IssueCreateSidePanelComponent onClose={onClose} flags={flags} />
			</AIEventsInstrumentationProvider>
		</SingleIssueCreateProvidersWrapper>
	);
};

const IssueCreateSidePanelWithFlags = withFlags(IssueCreateSidePanelComponentWithProvider);

const IssueCreateSidePanelWithErrorBoundary = withErrorBoundary({
	attribution: Attribution.INTEGRATIONS,
})(IssueCreateSidePanelWithFlags);

export const IssueCreateSidePanel = componentWithFG(
	'jira_ai_aiwc_in_confluence_set_up_error_boundary',
	IssueCreateSidePanelWithErrorBoundary,
	IssueCreateSidePanelWithFlags,
);
